import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * SARKY edit:
 * - Keeps the original Horizon cart-icon behavior.
 * - Adds a safe cart count sync after AJAX cart changes.
 * - Does not touch sticky ATC markup or header-actions markup.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount?.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    if (!this.refs.cartBubbleCount) return;
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    document.addEventListener('cart:refresh', this.syncCartCountFromCart);
    document.addEventListener('cart:open', this.syncCartCountFromCart);
    document.addEventListener('cart:updated', this.syncCartCountFromCart);

    document.documentElement.addEventListener('cart:refresh', this.syncCartCountFromCart);
    document.documentElement.addEventListener('cart:open', this.syncCartCountFromCart);
    document.documentElement.addEventListener('cart:updated', this.syncCartCountFromCart);

    this.ensureCartBubbleIsCorrect();
    window.setTimeout(this.syncCartCountFromCart, 250);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);

    document.removeEventListener('cart:refresh', this.syncCartCountFromCart);
    document.removeEventListener('cart:open', this.syncCartCountFromCart);
    document.removeEventListener('cart:updated', this.syncCartCountFromCart);

    document.documentElement.removeEventListener('cart:refresh', this.syncCartCountFromCart);
    document.documentElement.removeEventListener('cart:open', this.syncCartCountFromCart);
    document.documentElement.removeEventListener('cart:updated', this.syncCartCountFromCart);
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.syncCartCountFromCart();
    }
  };

  onCartUpdate = async (event) => {
    const itemCount = event.detail?.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail?.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  syncCartCountFromCart = async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: {
          Accept: 'application/json',
        },
        cache: 'no-store',
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = Number(cart?.item_count ?? 0);

      this.renderCartBubble(itemCount, false, true);
      this.updateAllCartCountText(itemCount);
    } catch (_) {
      // no-op
    }
  };

  updateAllCartCountText = (itemCount) => {
    const count = itemCount < 100 ? String(itemCount) : '';
    const show = itemCount > 0;

    const targets = document.querySelectorAll(
      [
        '[data-testid="cart-count-live-region"]',
        '[data-cart-count-live-region]',
        '[data-cart-count]',
        '.cart-count',
        '.cart-count-bubble',
        '.cart-bubble__text-count',
        '.header__icon--cart .cart-count',
        '.header__icon--cart .cart-count-bubble',
        '[data-sarky-cart-drawer-count]',
        '[data-sarky-cart-drawer-bubble]',
      ].join(',')
    );

    targets.forEach((target) => {
      if (!target) return;

      if (
        target.matches('[data-testid="cart-count-live-region"], [data-cart-count-live-region]')
      ) {
        target.textContent = show ? `Total items in cart: ${itemCount}` : '';
        return;
      }

      if (target.hasAttribute('data-sarky-cart-drawer-bubble')) {
        target.classList.toggle('hidden', !show);
        target.style.display = show ? '' : 'none';
        return;
      }

      if (target.hasAttribute('data-sarky-cart-drawer-count')) {
        target.textContent = count;
        return;
      }

      if (
        target.classList.contains('cart-bubble__text-count') ||
        target.hasAttribute('data-cart-count') ||
        target.classList.contains('cart-count')
      ) {
        target.textContent = count;
      }

      if (
        target.classList.contains('cart-count-bubble') ||
        target.classList.contains('cart-count')
      ) {
        target.classList.toggle('hidden', !show);
        target.style.display = show ? '' : 'none';
      }
    });

    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', show);
    });
  };

  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    if (!this.refs.cartBubble || !this.refs.cartBubbleCount || !this.refs.cartBubbleText) return;

    const nextCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    const hasItems = nextCount > 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', !hasItems);
    this.refs.cartBubble.classList.toggle('visually-hidden', !hasItems);

    this.currentCartCount = nextCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', hasItems);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !hasItems) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
          this.updateAllCartCountText(count);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}